import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { usePage } from '../hooks/usePage';
import { getDashboard } from '../store/slices/auth';

const BODY_CLASS = "bg-slate-950 text-slate-100 min-h-screen flex flex-col font-sans selection:bg-blue-600 selection:text-white";

// Full-page navigation: the target pages run live map controllers that expect a fresh document.
const go = (url) => window.location.assign(url);

export default function Home() {
  usePage({
    title: 'Election Police Live Location Tracking System - Main Portal',
    bodyClass: BODY_CLASS
  });

  const dispatch = useDispatch();

  const {
    dashboard,
  } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboard());
    import('../legacy/uploadModal.js');
  }, [dispatch]);

  const metrics = {
    totalPollingStations: dashboard?.totalPollingStations ?? 0,
    hyperSensitiveCount: dashboard?.hyperSensitiveBooths ?? 0,
    specialUnitsCount: dashboard?.specialTacticalUnits ?? 0,
    atStation: dashboard?.atPollingStation ?? 0,
    away: dashboard?.awayOfficers ?? 0,
    sos: dashboard?.activeSOS ?? 0,
  };

  // Keep your existing return JSX below this point.
  usePage({ title: 'Election Police Live Location Tracking System - Main Portal', bodyClass: BODY_CLASS });


  return (
    <>
      {/* TOP HEADER */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between shrink-0 shadow-lg sticky top-0 z-50">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-blue-700 via-indigo-600 to-blue-500 flex items-center justify-center text-white shadow-xl shadow-blue-900/40 text-2xl font-black">
            🇮🇳
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-black tracking-tight text-white uppercase">
                Nellore Police
              </h1>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                {' '}1,520 STATIONS SECURED
              </span>
            </div>
            {/* <p className="text-xs text-slate-400">
              State Election Commission & Police Operations Command • Automated Geofencing, Live GPS & SOS Dispatch
            </p> */}
          </div>
        </div>
        {/* 2. ICONS-WISE PORTAL NAVIGATION BAR (Proper Website Style) */}
        <nav className="hidden lg:flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold shadow-inner">
          <a href="/admin" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🗺️
            </span>
            <span>
              Admin Command
            </span>
          </a>
          <a href="/emergency-sos" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🚨
            </span>
            <span>
              Incident Grid
            </span>
          </a>
          <a href="/sector-patrol" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🚓
            </span>
            <span>
              Sector Patrol
            </span>
          </a>
          <a href="/officer-app" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              📱
            </span>
            <span>
              Staff App
            </span>
          </a>
        </nav>
        {/* Quick Navigation Links & Theme Switcher */}
        <div className="flex items-center gap-2.5">
          <button onClick={(event) => { openApkModal() }} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold shadow-md transition flex items-center gap-1.5">
            <span>
              📲
            </span>
            {' '}Staff App APK
          </button>
          {/* Pleasant Color Theme Switcher */}
          <ThemeSwitcher className="mr-0.5" labelClass="hidden xl:inline" />
          <div className="h-6 w-px bg-slate-800 mx-0.5"></div>
          <Link to="/login" className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-xs font-bold transition flex items-center gap-1.5">
            <span>
              🚪
            </span>
            {' '}Login
          </Link>
        </div>
      </header>
      {/* HERO SECTION WITH LIVE DISTRICT METRICS */}
      <section className="bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950 px-6 py-8 border-b border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <span className="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-bold rounded-full uppercase tracking-wider mb-3">
              Complete Election Police Command Hierarchy & 24h Tracking
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight leading-tight">
              Live Location Tracking, 24-Hour Movement History & Hierarchical Superior Alerting
            </h2>

            {/* Quick Upload & Operations CTA Buttons */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
              <button type="button" onClick={(event) => { openUploadModal('BOOTHS') }} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-lg transition flex items-center gap-2">
                <span>
                  📤
                </span>
                {' '}Upload Polling Stations (Lat/Lng)
              </button>
              <button type="button" onClick={(event) => { openUploadModal('SPECIAL_DUTIES') }} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold shadow-lg transition flex items-center gap-2">
                <span>
                  🛡️
                </span>
                {' '}Upload All Duty Wings
              </button>

              <a href="/emergency-sos" className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold shadow-lg transition flex items-center gap-2">
                <span>
                  🚨
                </span>
                {' '}Incident Command
              </a>
            </div>
          </div>
          {/* LIVE KPI METRIC CARDS (CLICK TO OPEN DIRECTLY IN GRID VIEW) */}

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-2">

            {/* TOTAL POLLING STATIONS */}
            <div
              onClick={() => go('/emergency-sos?category=ALL_BOOTHS')}
              title="Open All Booths in Grid View"
              className="cursor-pointer bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:border-blue-400 rounded-2xl p-3.5 shadow-lg transition"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Total Polling Stations
              </div>
              <div className="text-2xl font-black text-white mt-1">
                {metrics.totalPollingStations.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                100% Allotted Police
              </div>
            </div>

            {/* HYPER SENSITIVE */}
            <div
              onClick={() => go('/emergency-sos?category=HYPER_SENSITIVE')}
              title="Open Hyper Sensitive in Grid View"
              className="cursor-pointer bg-rose-950/30 hover:bg-rose-900/40 border border-rose-600/40 hover:border-rose-400 rounded-2xl p-3.5 shadow-lg transition"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-400">
                Hyper Sensitive
              </div>
              <div className="text-2xl font-black text-rose-400 mt-1">
                {metrics.hyperSensitiveCount.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-rose-500/80 mt-0.5">
                🔥 3 Armed Guards Req.
              </div>
            </div>

            {/* SPECIAL TACTICAL UNITS */}
            <div
              onClick={() => go('/emergency-sos?category=SPECIAL_UNITS')}
              title="Open Special Units in Grid View"
              className="cursor-pointer bg-cyan-950/30 hover:bg-cyan-900/40 border border-cyan-600/40 hover:border-cyan-400 rounded-2xl p-3.5 shadow-lg transition"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                Special Tactical Units
              </div>
              <div className="text-2xl font-black text-cyan-300 mt-1">
                {metrics.specialUnitsCount.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-cyan-500/80 mt-0.5">
                ⚡ 10 Formations / Wings
              </div>
            </div>

            {/* AT POLLING STATION */}
            <div
              onClick={() => go('/emergency-sos?category=ON_SITE')}
              title="Open On-Site in Grid View"
              className="cursor-pointer bg-emerald-950/20 hover:bg-emerald-900/30 border border-emerald-600/30 hover:border-emerald-400 rounded-2xl p-3.5 shadow-lg transition"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                At Polling Station
              </div>
              <div className="text-2xl font-black text-emerald-400 mt-1">
                {metrics.atStation.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-emerald-500/80 mt-0.5">
                ✓ Geofence Verified
              </div>
            </div>

            {/* AWAY / DEVIATED */}
            <div
              onClick={() => go('/emergency-sos?category=AWAY')}
              title="Open Away in Grid View"
              className="cursor-pointer bg-amber-950/20 hover:bg-amber-900/30 border border-amber-600/30 hover:border-amber-400 rounded-2xl p-3.5 shadow-lg transition"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
                Away / Deviated
              </div>
              <div className="text-2xl font-black text-amber-400 mt-1">
                {metrics.away.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-amber-500/80 mt-0.5">
                ⚠ Distance Alert Triggered
              </div>
            </div>

            {/* ACTIVE SOS ALERTS */}
            <div
              onClick={() => go('/emergency-sos?category=SOS')}
              title="Open Emergency SOS in Grid View"
              className="cursor-pointer bg-red-950/30 hover:bg-red-900/50 border border-red-500/50 hover:border-red-400 rounded-2xl p-3.5 shadow-lg animate-sos-strobe transition"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-red-400">
                Active SOS Alerts
              </div>
              <div className="text-2xl font-black text-red-500 mt-1">
                {metrics.sos.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-red-400 font-bold mt-0.5">
                🚨 Flying Squad Dispatched
              </div>
            </div>

          </div>

        </div>
      </section>
      {/* THREE PORTALS SHOWCASE */}
      <section className="flex-1 px-6 py-10 max-w-6xl mx-auto w-full">
        <div className="text-center mb-8">
          <h3 className="text-xl font-extrabold text-white">
            Hierarchical Police Operations & Tracking Portals
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Access District Admin (full district authority) or log in as DSP, Circle Inspector, Station SHO, or Tactical Wing Commander:
          </p>
          <div className="mt-2.5">
            <Link to="/login" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/40 text-amber-300 font-bold text-xs hover:bg-amber-500/20 transition">
              <span>
                👑
              </span>
              {' '}Authenticate as SP Admin, DSP, CI, SI, or Tactical Wing Commander →
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* CARD 1: ADMIN WEB APPLICATION */}
          <div className="bg-slate-900/90 border border-slate-800 hover:border-blue-500/60 rounded-3xl p-5 flex flex-col justify-between shadow-xl transition duration-300 group">
            <div>
              <div className="w-11 h-11 rounded-2xl bg-blue-600/20 text-blue-400 border border-blue-500/30 flex items-center justify-center text-xl font-bold mb-3 group-hover:scale-110 transition">
                🛡️
              </div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400">
                District HQ Portal
              </span>
              <h4 className="text-base font-bold text-white mt-1">
                Admin Command Center
              </h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                Full control room oversight: Monitor all 15 Supervisory Officers and 1,520+ Police Men. Includes CSV upload/modify/delete.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}
                  <strong>
                    Upload CSV
                  </strong>
                  {' '}(Bulk stations & staff)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}
                  <strong>
                    Modify & Delete
                  </strong>
                  {' '}any station
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}Live Map with Google Hybrid layer
                </li>
              </ul>
            </div>
            <div className="pt-4 mt-4 border-t border-slate-800">
              <a href="/admin" className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-blue-900/50 flex items-center justify-center gap-1.5 transition">
                <span>
                  Launch Admin Center
                </span>
                {' '}→
              </a>
            </div>
          </div>
          {/* CARD 2: EMERGENCY SOS & OPERATIONS GRID */}
          <div className="bg-slate-900/90 border border-red-500/40 hover:border-red-500 rounded-3xl p-5 flex flex-col justify-between shadow-xl transition duration-300 group">
            <div>
              <div className="w-11 h-11 rounded-2xl bg-red-600/20 text-red-400 border border-red-500/30 flex items-center justify-center text-xl font-bold mb-3 group-hover:scale-110 transition animate-pulse">
                🚨
              </div>
              <span className="text-[10px] uppercase font-black tracking-wider text-red-400">
                Live Incident Center
              </span>
              <h4 className="text-base font-bold text-white mt-1">
                Incident & Operations Grid
              </h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                Displays WHO clicked Emergency SOS & WHEN. View all dashboard numbers in responsive Grid View with QRT force dispatch, info pass-on & two-way chat.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="text-red-400 font-bold">
                    ⚡
                  </span>
                  {' '}
                  <strong>
                    Dispatch Striking Force / QRT
                  </strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-indigo-400 font-bold">
                    📢
                  </span>
                  {' '}
                  <strong>
                    Pass On Alert
                  </strong>
                  {' '}to DSP, CI, SI
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    💬
                  </span>
                  {' '}
                  <strong>
                    Two-Way Messages & Replies
                  </strong>
                </li>
              </ul>
            </div>
            <div className="pt-4 mt-4 border-t border-slate-800">
              <a href="/emergency-sos" className="w-full py-2.5 bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600 text-white rounded-xl text-xs font-bold shadow-lg shadow-red-900/50 flex items-center justify-center gap-1.5 transition">
                <span>
                  Launch Incident Grid
                </span>
                {' '}→
              </a>
            </div>
          </div>
          {/* CARD 3: SUPERVISORY OFFICER TRAVELING & MEN MONITORING */}
          <div className="bg-slate-900/90 border border-slate-800 hover:border-indigo-500/60 rounded-3xl p-5 flex flex-col justify-between shadow-xl transition duration-300 group">
            <div>
              <div className="w-11 h-11 rounded-2xl bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 flex items-center justify-center text-xl font-bold mb-3 group-hover:scale-110 transition">
                🚓
              </div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-indigo-400">
                Mobile Commander
              </span>
              <h4 className="text-base font-bold text-white mt-1">
                Supervisory Patrol Portal
              </h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                DSPs, Circle Inspectors & SIs travel booth-to-booth with smooth moving patrol vehicle animation and direct subordinate monitoring.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}Station-to-station route animation
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}
                  <strong>
                    "My Men on Duty"
                  </strong>
                  {' '}live GPS
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}Booth check-in verification logs
                </li>
              </ul>
            </div>
            <div className="pt-4 mt-4 border-t border-slate-800">
              <a href="/sector-patrol" className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-indigo-900/50 flex items-center justify-center gap-1.5 transition">
                <span>
                  Launch Patrol Portal
                </span>
                {' '}→
              </a>
            </div>
          </div>
          {/* CARD 4: POLICE PERSONNEL MOBILE APP */}
          <div className="bg-slate-900/90 border border-slate-800 hover:border-emerald-500/60 rounded-3xl p-5 flex flex-col justify-between shadow-xl transition duration-300 group">
            <div>
              <div className="w-11 h-11 rounded-2xl bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center text-xl font-bold mb-3 group-hover:scale-110 transition">
                📱
              </div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-400">
                Field Police Sentry
              </span>
              <h4 className="text-base font-bold text-white mt-1">
                Police Staff Mobile App
              </h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                Used by police constables at assigned booths. Continuous GPS geofencing, non-attendance alert, SOS alarm & two-way reply messaging.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}Real Phone GPS + Simulator
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}🚨 One-touch Emergency SOS
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">
                    ✓
                  </span>
                  {' '}💬 Direct reply to SI & Admin
                </li>
              </ul>
            </div>
            <div className="pt-4 mt-4 border-t border-slate-800">
              <a href="/officer-app?officer=POL-12" className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-emerald-900/50 flex items-center justify-center gap-1.5 transition">
                <span>
                  Launch Staff App
                </span>
                {' '}→
              </a>
            </div>
          </div>
        </div>

      </section>
      {/* FOOTER */}
      <footer className="bg-slate-900 border-t border-slate-800 py-4 px-6 text-center text-xs text-slate-500">
        Election Commission of India & State Police Command • Election Police Live Location Tracking System
      </footer>
      {/* SCRIPTS */}
    </>
  );
}
